import React, { useState, useEffect, useMemo } from 'react';
import { useExport } from './header/exporters';
import idColumn, { RecordIdLabel } from './ui/idColumn';
import { formatDate } from '../utils/formatDate';
import { daysUntil, localDayKey } from '../utils/localDate';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { MetricCard } from './MetricCards';
import { refreshBudgetFromApi, BUDGET_CHANGE_EVENT, netPaid } from '../utils/budgetStorage';
import { BudgetCategory, Payment, PaymentStatus } from '../types/ui/Budget';
import { useSettings } from '../contexts/SettingsContext';
import ContributorDashboard from './ContributorDashboard';
import SettlementReconciliation from './SettlementReconciliation';
import StatusBadge, { StatusBadgeVariant } from './ui/StatusBadge';
import { MaskedValue } from './ui/MaskedValue';
import DataTable from './DataTable';
import '../styles/components/PaymentsView.css';
import { fmtId } from '../utils/jiraId';
import { entityPath } from '../utils/entityRoutes';
import { useNotifications } from './NotificationProvider';
import SortableHeader from './SortableHeader';
import EmptyState from './EmptyState';

type PaymentsTab = 'payments' | 'contributors' | 'settlement';

interface PaymentRow {
  id: string;
  categoryId: string;
  vendor: string;
  /** vendors.id, when this budget line is linked to a vendor. */
  vendorRef?: string | null;
  category: string;
  service: string;
  totalCost: number;
  totalPaid: number;
  balance: number;
  paymentStatus: PaymentStatus;
  lastPaymentDate: string | null;
  payments: Payment[];
  depositAmount?: number;
  depositDueDate?: string;
  balanceDueDate?: string;
}

type FilterStatus = 'all' | 'not-started' | 'partially-paid' | 'fully-paid' | 'overdue';
type SortField = 'vendor' | 'balance' | 'totalCost' | 'lastPaymentDate';

// Only vendors the couple has actually committed to are payment obligations —
// quotes, inquiries, and rejected options are research, not debts.
const COMMITTED_STATUSES = ['booked', 'contract-signed', 'paid-in-full'];

/**
 * Distinguishes "the required deposit has actually cleared" from a
 * generic partial payment — a schedule-aware status, not just a paid/total
 * ratio. Falls back to the old binary behaviour when a vendor has no
 * deposit_amount on record (most vendors, until backfilled from contracts).
 */
function derivePaymentScheduleStatus(
  totalPaid: number,
  totalCost: number,
  depositAmount: number | undefined,
  depositDueDate: string | undefined,
  balanceDueDate: string | undefined
): PaymentStatus {
  if (totalPaid <= 0) return 'not-started';
  if (totalPaid >= totalCost) return 'fully-paid';

  const status: PaymentStatus =
    depositAmount != null && totalPaid >= depositAmount ? 'deposit-paid' : 'partially-paid';

  // Overdue trumps the above once a due date has actually passed —
  // the deposit not yet cleared by its due date, or the balance not yet
  // cleared by its due date, both count.
  const today = localDayKey();
  const depositOverdue = status === 'partially-paid' && !!depositDueDate && depositDueDate < today;
  const balanceOverdue = !!balanceDueDate && balanceDueDate < today;
  if (depositOverdue || balanceOverdue) return 'overdue';

  return status;
}

interface PaymentsViewProps {
  filterStatus?: string;
  onFilterStatusChange?: (status: string) => void;
  sortField?: string;
  onSortFieldChange?: (field: string) => void;
  paymentsTab?: PaymentsTab;
  onPaymentsTabChange?: (tab: PaymentsTab) => void;
  eventFilter?: string;
}

const PaymentsView: React.FC<PaymentsViewProps> = ({
  filterStatus: externalFilterStatus = 'all',
  onFilterStatusChange: _onFilterStatusChange,
  sortField: externalSortField = 'balance',
  onSortFieldChange,
  paymentsTab = 'payments',
  onPaymentsTabChange,
  eventFilter = '',
}) => {
  const navigate = useNavigate();
  const { showSuccess } = useNotifications();
  const { formatCurrency, formatCurrencyCompact } = useSettings();
  const activeTab = paymentsTab;
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const filterStatus = externalFilterStatus as FilterStatus;
  const sortField = externalSortField as SortField;
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);
  useEffect(() => {
    void refreshBudgetFromApi().then(setCategories).catch(() => {});
    const handler = () => void refreshBudgetFromApi().then(setCategories).catch(() => {});
    window.addEventListener(BUDGET_CHANGE_EVENT, handler);
    return () => window.removeEventListener(BUDGET_CHANGE_EVENT, handler);
  }, []);

  // Same event_ids[] / legacy event_id fallback chain BudgetTracker uses —
  // Payments shows the same underlying vendor data, so it should scope the
  // same way when an event filter is active instead of always aggregating
  // across every event unconditionally.
  const scopedCategories = useMemo(() => {
    if (!eventFilter) return categories;
    return categories
      .map(c => ({
        ...c,
        vendors: c.vendors.filter(v => {
          const catEventIds = c.event_ids?.length ? c.event_ids : (c.event_id ? [c.event_id] : []);
          return catEventIds.includes(eventFilter) || v.event_id === eventFilter;
        }),
      }))
      .filter(c => c.vendors.length > 0);
  }, [categories, eventFilter]);

  const vendorPaymentRows: PaymentRow[] = useMemo(() => {
    const rows: PaymentRow[] = [];
    for (const cat of scopedCategories) {
      for (const v of cat.vendors) {
        const isCommitted =
          COMMITTED_STATUSES.includes(v.booking_status ?? '') ||
          v.payments.some(p => p.status === 'completed');
        if (!isCommitted) continue;
        // The line's cost is its planned cost; actual_cost is what has been paid.
        const totalCost = v.plannedCostBase;
        const totalPaid = netPaid(v.payments);
        const lastCompleted = v.payments
          .filter(p => p.status === 'completed')
          .sort((a, b) => b.date.localeCompare(a.date))[0];
        const computedStatus = derivePaymentScheduleStatus(totalPaid, totalCost, v.depositAmount, v.depositDueDate, v.balanceDueDate);
        rows.push({
          id: v.vendor_id,
          categoryId: String(cat.id),
          vendor: v.vendor_name,
          vendorRef: v.vendor_ref,
          category: cat.name,
          service: v.service,
          totalCost,
          totalPaid,
          balance: totalCost - totalPaid,
          paymentStatus: computedStatus,
          lastPaymentDate: lastCompleted?.date ?? null,
          payments: v.payments,
          depositAmount: v.depositAmount,
          depositDueDate: v.depositDueDate,
          balanceDueDate: v.balanceDueDate,
        });
      }
    }
    return rows;
  }, [scopedCategories]);

  const paymentRows = vendorPaymentRows;

  const filtered = useMemo(() => {
    let rows = filterStatus === 'all'
      ? paymentRows
      : paymentRows.filter(r => r.paymentStatus === filterStatus);

    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'vendor': cmp = a.vendor.localeCompare(b.vendor); break;
        case 'balance': cmp = a.balance - b.balance; break;
        case 'totalCost': cmp = a.totalCost - b.totalCost; break;
        case 'lastPaymentDate':
          cmp = (a.lastPaymentDate ?? '').localeCompare(b.lastPaymentDate ?? '');
          break;
      }
      return sortAsc ? cmp : -cmp;
    });

    return rows;
  }, [paymentRows, filterStatus, sortField, sortAsc]);

  const summary = useMemo(() => {
    const totalBudget = paymentRows.reduce((s, r) => s + r.totalCost, 0);
    const totalPaid = paymentRows.reduce((s, r) => s + r.totalPaid, 0);
    const totalOutstanding = paymentRows.reduce((s, r) => s + r.balance, 0);
    const overdue = paymentRows.filter(r => r.paymentStatus === 'overdue').length;
    const fullyPaid = paymentRows.filter(r => r.paymentStatus === 'fully-paid').length;
    return { totalBudget, totalPaid, totalOutstanding, overdue, fullyPaid, total: paymentRows.length };
  }, [paymentRows]);

  // Next unmet milestone (deposit or balance) per vendor — surfaced as a
  // small icon in that vendor's own row (pv-row-actions) rather than a
  // separate list, so it reads as "this vendor has something coming up"
  // right where you'd already be looking to pay them.
  interface ScheduleEntry {
    id: string;
    milestone: 'Deposit' | 'Balance';
    amountDue: number;
    dueDate: string;
    overdue: boolean;
  }
  const upcomingByVendor: Map<string, ScheduleEntry> = useMemo(() => {
    const today = localDayKey();
    const map = new Map<string, ScheduleEntry>();
    for (const r of vendorPaymentRows) {
      if (r.paymentStatus === 'fully-paid') continue;
      const depositTarget = r.depositAmount ?? r.totalCost;
      const depositMet = r.totalPaid >= depositTarget;
      if (!depositMet && r.depositDueDate) {
        map.set(r.id, {
          id: r.id, milestone: 'Deposit',
          amountDue: depositTarget - r.totalPaid, dueDate: r.depositDueDate,
          overdue: r.depositDueDate < today,
        });
      } else if (depositMet && r.balance > 0 && r.balanceDueDate) {
        map.set(r.id, {
          id: r.id, milestone: 'Balance',
          amountDue: r.balance, dueDate: r.balanceDueDate,
          overdue: r.balanceDueDate < today,
        });
      }
    }
    return map;
  }, [vendorPaymentRows]);

  // Groups upcomingByVendor's next-milestone-per-vendor into "how soon" bands
  // — the deposit/balance due dates already exist on every booking, but
  // nothing surfaced them as a single at-a-glance timeline; each only showed
  // up as a small hint on its own vendor's row, buried in the table below.
  interface TimelineBucket {
    key: string;
    label: string;
    entries: (ScheduleEntry & { vendorName: string })[];
    total: number;
  }
  const dueTimeline: TimelineBucket[] = useMemo(() => {
    const nameById = new Map(vendorPaymentRows.map(r => [r.id, r.vendor]));
    const bands: TimelineBucket[] = [
      { key: 'overdue', label: 'Overdue', entries: [], total: 0 },
      { key: '0-3', label: 'Next 3 months', entries: [], total: 0 },
      { key: '3-12', label: '3–12 months', entries: [], total: 0 },
      { key: '12+', label: '12+ months', entries: [], total: 0 },
    ];
    for (const entry of upcomingByVendor.values()) {
      const vendorName = nameById.get(entry.id) ?? 'Unknown vendor';
      const monthsOut = (daysUntil(entry.dueDate) ?? 0) / 30;
      const bucket = entry.overdue ? bands[0] : monthsOut <= 3 ? bands[1] : monthsOut <= 12 ? bands[2] : bands[3];
      bucket.entries.push({ ...entry, vendorName });
      bucket.total += entry.amountDue;
    }
    for (const b of bands) b.entries.sort((a, b2) => a.dueDate.localeCompare(b2.dueDate));
    return bands.filter(b => b.entries.length > 0);
  }, [upcomingByVendor, vendorPaymentRows]);

  const statusLabel = (s: PaymentStatus) => {
    switch (s) {
      case 'not-started': return 'Not Started';
      case 'deposit-paid': return 'Deposit Paid';
      case 'partially-paid': return 'Partially Paid';
      case 'fully-paid': return 'Fully Paid';
      case 'overdue': return 'Overdue';
      default: return s;
    }
  };

  const PAYMENT_DS: Record<string, string> = { 'not-started': 'neutral', 'deposit-paid': 'info', 'partially-paid': 'warning', 'fully-paid': 'success', overdue: 'danger' };

  // Export button (Header): the rows this list shows right now.
  useExport('payments', {
    rows: filtered,
    ctx: { statusLabel: s => statusLabel(s as PaymentStatus), date: d => formatDate(d) },
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(prev => !prev);
    } else {
      onSortFieldChange?.(field);
      setSortAsc(false);
    }
  };

  return (
    <div className="wt-container fade-in">
      {activeTab === 'contributors' && <ContributorDashboard />}
      {activeTab === 'settlement' && <SettlementReconciliation />}

      {activeTab === 'payments' && <>
        {/* Summary Cards */}
        <section className="stats-grid">
          <MetricCard label="Total Budget" value={formatCurrencyCompact(summary.totalBudget)} tone="blue" icon="wallet" />
          <MetricCard label="Total Paid" value={formatCurrencyCompact(summary.totalPaid)} tone="green" icon="check" />
          <MetricCard label="Outstanding" value={formatCurrencyCompact(summary.totalOutstanding)} tone="orange" icon="clock" />
          <MetricCard label="Vendors Paid in Full" value={`${summary.fullyPaid} / ${summary.total}`} tone="teal" icon="briefcase" />
          <MetricCard
            label="Payment Progress"
            value={summary.totalBudget > 0 ? `${Math.round((summary.totalPaid / summary.totalBudget) * 100)}%` : '0%'}
            tone={summary.totalBudget > 0 && summary.totalPaid / summary.totalBudget >= 0.8 ? 'green' : summary.totalBudget > 0 && summary.totalPaid / summary.totalBudget >= 0.5 ? 'teal' : 'orange'}
            icon="chart"
          />
          {summary.overdue > 0 && (
            <MetricCard label="Overdue" value={summary.overdue} tone="pink" icon="alert-triangle" />
          )}
        </section>

        {/* Payment Timeline — every deposit/balance due date already existed
            on each vendor row, but only as a hint buried in that vendor's own
            row further down; grouping them by how soon they're due answers
            "what's coming up" at a glance instead of scanning the whole table. */}
        {dueTimeline.length > 0 && (
          <section className="card pv-timeline">
            <div className="card-header">
              <h3>Payment Timeline</h3>
            </div>
            <div className="card-body pv-timeline-bands">
              {dueTimeline.map(band => (
                <div key={band.key} className="pv-timeline-band">
                  <div className="pv-timeline-band-header">
                    <span className={band.key === 'overdue' ? 'pv-balance-due' : ''}>{band.label}</span>
                    <strong>{formatCurrencyCompact(band.total)}</strong>
                  </div>
                  <ul className="pv-timeline-entries">
                    {band.entries.map(entry => (
                      <li key={entry.id} className="pv-timeline-entry">
                        <span className="pv-timeline-entry-vendor">{entry.vendorName}</span>
                        <span className="pv-timeline-entry-detail">
                          {entry.milestone} · {formatCurrency(entry.amountDue)} · {formatDate(entry.dueDate)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Payment Table */}
        {filtered.length === 0 ? (
          <EmptyState
            icon="wallet"
            title="No payment records"
            description="Add vendors to your budget to start tracking payments here."
          />
        ) : (
          <div className="pv-table-scroll ds-scroll-x ds-scroll-x--framed">
            <table className="dt dt--hoverable">
              <thead>
                <tr>
                  <th>ID</th>
                  <SortableHeader
                    label="Vendor"
                    sortKey="vendor"
                    sortConfig={{ field: sortField, direction: sortAsc ? 'asc' : 'desc' }}
                    onSort={key => handleSort(key as SortField)}
                  />
                  <th>Category</th>
                  <SortableHeader
                    label="Total Cost"
                    sortKey="totalCost"
                    sortConfig={{ field: sortField, direction: sortAsc ? 'asc' : 'desc' }}
                    onSort={key => handleSort(key as SortField)}
                  />
                  <th>Paid</th>
                  <SortableHeader
                    label="Balance"
                    sortKey="balance"
                    sortConfig={{ field: sortField, direction: sortAsc ? 'asc' : 'desc' }}
                    onSort={key => handleSort(key as SortField)}
                  />
                  <th>Status</th>
                  <SortableHeader
                    label="Last Payment"
                    sortKey="lastPaymentDate"
                    sortConfig={{ field: sortField, direction: sortAsc ? 'asc' : 'desc' }}
                    onSort={key => handleSort(key as SortField)}
                  />
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => {
                  const displayPayments = row.payments;
                  return (
                  <React.Fragment key={row.id}>
                    <tr
                      className={`pv-row ${expandedVendor === row.id ? 'pv-row-expanded' : ''}${row.paymentStatus === 'fully-paid' ? ' pv-row-settled' : ''}`}
                      onClick={() => setExpandedVendor(prev => prev === row.id ? null : row.id)}
                    >
                      <td className="rec-id-cell"><RecordIdLabel kind="vendor" id={row.id} /></td>
                      <td>
                        <div className="pv-vendor-cell">
                          <Icon name={expandedVendor === row.id ? 'chevronDown' : 'chevronRight'} size={14} />
                          <div>
                            {(() => {
                              // The vendor's name should open the vendor, not
                              // the budget category it happens to be filed
                              // under — that's what the Category cell is for.
                              // Only a line with vendor_ref set has a vendor
                              // page to open; the rest (contingency lines,
                              // one-off costs) stay plain text rather than
                              // offering a link that goes nowhere.
                              const vendorHref = entityPath('vendor', row.vendorRef);
                              if (!vendorHref) return <span className="pv-vendor-name">{row.vendor}</span>;
                              return (
                                <button
                                  className="pv-vendor-link"
                                  onClick={e => { e.stopPropagation(); navigate(vendorHref); }}
                                  title={`Open ${row.vendor}`}
                                >
                                  {row.vendor}
                                </button>
                              );
                            })()}
                            {row.paymentStatus !== 'fully-paid' && (
                              <>
                                {(row.paymentStatus === 'not-started' || row.paymentStatus === 'partially-paid') && row.depositDueDate && (
                                  <div className="pv-vendor-schedule-hint">Deposit due {formatDate(row.depositDueDate)}</div>
                                )}
                                {(row.paymentStatus === 'deposit-paid' || row.paymentStatus === 'overdue') && row.balanceDueDate && (
                                  <div className="pv-vendor-schedule-hint">Balance due {formatDate(row.balanceDueDate)}</div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{row.category}</td>
                      <td className="pv-cost-settled">{formatCurrency(row.totalCost)}</td>
                      <td className="pv-paid">{formatCurrency(row.totalPaid)}</td>
                      <td className={row.balance > 0 ? 'pv-balance-due' : 'pv-balance-clear'}>
                        {formatCurrency(row.balance)}
                      </td>
                      <td><StatusBadge variant={(PAYMENT_DS[row.paymentStatus] ?? 'neutral') as StatusBadgeVariant}>{statusLabel(row.paymentStatus)}</StatusBadge></td>
                      <td>{formatDate(row.lastPaymentDate)}</td>
                      <td>
                        <div className="pv-row-actions">
                          {upcomingByVendor.has(row.id) && (() => {
                            const entry = upcomingByVendor.get(row.id)!;
                            return (
                              <span
                                className={`pv-upcoming-badge${entry.overdue ? ' pv-upcoming-badge--overdue' : ''}`}
                                title={`${entry.milestone} ${entry.overdue ? 'overdue since' : 'due'} ${formatDate(entry.dueDate)} — ${formatCurrencyCompact(entry.amountDue)}`}
                              >
                                <Icon name="clock" size={13} />
                              </span>
                            );
                          })()}
                          {displayPayments.length > 0 && (
                            <span className="pv-history-count" title="Payment history entries">{displayPayments.length}</span>
                          )}
                          {row.paymentStatus !== 'fully-paid' && (
                            <button
                              className="pv-pay-btn"
                              title="Record payment"
                              onClick={e => {
                                e.stopPropagation();
                                navigate(`/payments/record/new?vendor=${encodeURIComponent(row.id)}`);
                              }}
                            >
                              <Icon name="plus" size={13} />
                              Pay
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedVendor === row.id && displayPayments.length > 0 && (
                      <tr className="pv-detail-row">
                        <td colSpan={9}>
                          <div className="pv-payment-history">
                            <DataTable<Payment>
                              recordList={{ kind: 'payment' }}
                              data={displayPayments}
                              className="pv-history-table"
                              rowClassName={p => (p.entryType === 'refund' ? 'pv-refund-row' : '')}
                              // The row navigates to the payment, replacing a
                              // trailing edit-icon column that did the same
                              // thing in 48px of table width.
                              onRowClick={p => navigate(`/payments/record/${fmtId('PY', p.id)}`, { state: { editPayment: p, vendorId: row.id } })}
                              columns={[
                                idColumn<Payment>('payment'),
                                { key: 'date', header: 'Date', render: (_v, p) => formatDate(p.date) },
                                {
                                  key: 'amount',
                                  header: 'Amount',
                                  render: (_v, p) => (
                                    <div className="pv-amount-cell">
                                      <span>{p.entryType === 'refund' ? `−${formatCurrency(p.amount, p.currency)}` : formatCurrency(p.amount, p.currency)}</span>
                                      <span className="pv-method">{p.method.replace('-', ' ')}</span>
                                    </div>
                                  ),
                                },
                                { key: 'paid_by', header: 'Paid by', render: (_v, p) => p.paid_by || '—' },
                                {
                                  key: 'status',
                                  header: 'Status',
                                  render: (_v, p) => (
                                    <StatusBadge variant={p.status === 'completed' ? 'success' : p.status === 'pending' ? 'warning' : 'danger'}>
                                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                                    </StatusBadge>
                                  ),
                                },
                                {
                                  key: 'reference',
                                  header: 'Reference',
                                  render: (_v, p) => p.reference ? <MaskedValue value={p.reference} /> : <span>—</span>,
                                },
                                {
                                  key: 'notes',
                                  header: 'Notes',
                                  width: '150px',
                                  className: 'pv-col-notes',
                      clamp: 4,
                                  render: (_v, p) => (
                                    <span title={p.notes || undefined}>
                                      {p.entryType === 'refund' && <span className="pv-type-tag pv-refund-tag">Refund</span>}
                                      {p.notes ? p.notes : '—'}
                                    </span>
                                  ),
                                },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {expandedVendor === row.id && displayPayments.length === 0 && (
                      <tr className="pv-detail-row">
                        <td colSpan={9}>
                          <div className="pv-no-payments">No payments recorded for this vendor yet.</div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>}

    </div>
  );
};

export default PaymentsView;
